import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Note, Async } from '../components/Async';
import { useAuth } from '../lib/auth';
import {
  getPaymentConfig,
  getPaymentMethods,
  createSetupIntent,
  hsGetPaymentMethods,
  hsCreateSetup,
  getClientOptions,
  getPaymentsOverview,
  createCardLink,
  ApiError,
  type SavedCard,
  type PaymentConfig,
  type PaymentRail,
  type CardOverviewClient,
} from '../lib/api';
import { useFetch } from '../lib/useFetch';

// ── Stripe loader (memoized per key) ──
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(pk: string) {
  if (!stripePromise) stripePromise = loadStripe(pk);
  return stripePromise;
}

// ── Hyperswitch HyperLoader (dynamic script) ──
type HyperInstance = {
  widgets: (o: { clientSecret: string }) => { create: (t: string, o?: unknown) => { mount: (selector: string) => void } };
  confirmPayment: (o: unknown) => Promise<{ error?: { message?: string } }>;
};
let hyperPromise: Promise<(pk: string) => HyperInstance> | null = null;
function loadHyper(): Promise<(pk: string) => HyperInstance> {
  const w = window as unknown as { Hyper?: (pk: string) => HyperInstance };
  if (w.Hyper) return Promise.resolve(w.Hyper);
  if (!hyperPromise) {
    hyperPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      // Production HyperLoader. beta.hyperswitch.io is the sandbox build and
      // cannot drive a live pk_prd_ key: it crashed on the pay page with
      // "c.split is not a function" and never mounted the card form.
      s.src = 'https://checkout.hyperswitch.io/v1/HyperLoader.js';
      s.async = true;
      s.onload = () => (w.Hyper ? resolve(w.Hyper) : reject(new Error('Hyperswitch SDK unavailable')));
      s.onerror = () => reject(new Error('Failed to load Hyperswitch SDK'));
      document.head.appendChild(s);
    });
  }
  return hyperPromise;
}

export const PaymentMethodsPage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const { user, can } = useAuth();
  const isClinic = user?.role === 'clinic_view';

  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [rail, setRail] = useState<PaymentRail>('hyperswitch');
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [clientId, setClientId] = useState<string>(isClinic ? (user?.clientId ?? '') : '');
  const [cards, setCards] = useState<SavedCard[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPaymentConfig()
      .then((c) => {
        setConfig(c);
        // default to whichever rail is configured (hyperswitch = pharmacy first)
        setRail(c.hyperswitch.configured || !c.stripe.configured ? 'hyperswitch' : 'stripe');
      })
      .catch(() => setConfig({ stripe: { configured: false, publishableKey: null }, hyperswitch: { configured: false, publishableKey: null } }));
    if (!isClinic) {
      getClientOptions()
        .then((r) => {
          const list = r.clients.map((c) => ({ id: c.id, name: c.name }));
          setClients(list);
          setClientId((cur) => cur || list[0]?.id || '');
        })
        .catch(() => {});
    }
  }, [isClinic]);

  function loadCards(id: string, r: PaymentRail) {
    if (!id) return;
    setCards(null);
    const fn = r === 'hyperswitch' ? hsGetPaymentMethods : getPaymentMethods;
    fn(id)
      .then((res) => setCards(res.cards))
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : 'Could not load cards');
        setCards([]);
      });
  }
  useEffect(() => { loadCards(clientId, rail); setAdding(false); /* eslint-disable-next-line */ }, [clientId, rail]);

  const railCfg = config?.[rail];
  const railLabel = rail === 'hyperswitch' ? 'Hyperswitch (pharmacy)' : 'Stripe (platform)';

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Payment Methods"
        sub="Cards on file, saved for off-session charging."
        actions={
          can('payments:manage') && clientId && railCfg?.configured && railCfg.publishableKey ? (
            <button className="btn btn--primary" onClick={() => setAdding((a) => !a)}>
              {adding ? 'Cancel' : 'Add card'}
            </button>
          ) : undefined
        }
      />

      {!isClinic && <CardOverview />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>Processor:</label>
          <select className="select" value={rail} onChange={(e) => setRail(e.target.value as PaymentRail)}>
            <option value="hyperswitch">Hyperswitch (pharmacy)</option>
            <option value="stripe">Stripe (platform)</option>
          </select>
        </div>
        {!isClinic && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>Client:</label>
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {config && !railCfg?.configured && (
        <Note>{railLabel} isn’t configured yet (keys missing on the server).</Note>
      )}
      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>
      )}

      {adding && clientId && railCfg?.publishableKey && (
        rail === 'stripe' ? (
          <StripeAddCard publishableKey={railCfg.publishableKey} clientId={clientId} onDone={() => { setAdding(false); loadCards(clientId, rail); }} />
        ) : (
          <HsAddCard clientId={clientId} onDone={() => { setAdding(false); loadCards(clientId, rail); }} />
        )
      )}

      <PCard>
        {cards === null ? (
          <Note>Loading…</Note>
        ) : cards.length === 0 ? (
          <Note>No cards on file yet.</Note>
        ) : (
          <table className="p-table">
            <thead><tr><th>Card</th><th>Expires</th><th>Default</th></tr></thead>
            <tbody>
              {cards.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{(c.brand ?? 'card').toString().toUpperCase()} •••• {c.last4}</td>
                  <td className="muted tabular">{String(c.expMonth ?? '').padStart(2, '0')}/{c.expYear}</td>
                  <td>{c.isDefault && <StatusPill tone="success">Default</StatusPill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PCard>
    </Page>
  );
};

// ── Staff overview: who has a card on file + the card charged next, per rail ──
function railCell(c: CardOverviewClient, rail: PaymentRail) {
  const r = c.rails.find((x) => x.rail === rail);
  if (!r) return <span className="muted">—</span>; // not billed on this rail
  if (r.selected) {
    return (
      <span>
        <StatusPill tone="success">{(r.selected.brand ?? 'card').toString().toUpperCase()} •••• {r.selected.last4}</StatusPill>
        {r.cardCount > 1 && <span className="muted" style={{ fontSize: 11 }}> +{r.cardCount - 1}</span>}
      </span>
    );
  }
  return <StatusPill tone="error">No card</StatusPill>;
}

function CardOverview() {
  const state = useFetch(getPaymentsOverview);
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function copyLink(clientId: string) {
    setBusy(clientId);
    try {
      const { token } = await createCardLink(clientId);
      await navigator.clipboard.writeText(`${window.location.origin}/add-card/${token}`);
      setCopied(clientId);
      setTimeout(() => setCopied((c) => (c === clientId ? null : c)), 2500);
    } catch {
      /* clipboard/permission denied — ignore */
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="section-title">Cards on file</div>
      <div style={{ fontSize: 12, color: 'var(--p-muted-fg)', marginBottom: 8 }}>
        The card shown per rail is the one that will be charged for the next invoice. Send a client their link to add a card without a login.
      </div>
      <Async state={state} isEmpty={(d) => d.clients.length === 0} emptyLabel="No clients yet.">
        {(data) => (
          <PCard>
            <table className="p-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Platform (Stripe)</th>
                  <th>Pharmacy (Hyperswitch)</th>
                  <th style={{ textAlign: 'right' }}>Add-card link</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>
                      {c.name}
                      <div className="muted" style={{ fontSize: 11 }}>{c.type}</div>
                    </td>
                    <td>{railCell(c, 'stripe')}</td>
                    <td>{railCell(c, 'hyperswitch')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn" onClick={() => copyLink(c.id)} disabled={busy === c.id}>
                        {copied === c.id ? 'Copied!' : busy === c.id ? '…' : 'Copy link'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PCard>
        )}
      </Async>
    </div>
  );
}

// ── Stripe card capture (Elements) ──
function StripeAddCard({ publishableKey, clientId, onDone }: { publishableKey: string; clientId: string; onDone: () => void }) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    createSetupIntent(clientId)
      .then((r) => setClientSecret(r.clientSecret))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not start card setup'));
  }, [clientId]);

  const stripe = useMemo(() => getStripe(publishableKey), [publishableKey]);
  if (error) return <PCard><div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div></PCard>;
  if (!clientSecret) return <PCard><Note>Preparing secure card form…</Note></PCard>;

  return (
    <PCard>
      <Elements stripe={stripe} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
        <StripeCardForm onDone={onDone} />
      </Elements>
    </PCard>
  );
}

function StripeCardForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    setBusy(false);
    if (err) setError(err.message ?? 'Card could not be saved');
    else onDone();
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
      <button type="submit" className="btn btn--primary" disabled={!stripe || busy} style={{ marginTop: 14 }}>
        {busy ? 'Saving…' : 'Save card'}
      </button>
    </form>
  );
}

// ── Hyperswitch card capture (HyperLoader). Verify the widgets/confirmPayment
// API against the sandbox once creds are set. ──
function HsAddCard({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const mountRef = useRef<HTMLDivElement>(null);
  // HyperLoader's mount() resolves a CSS selector itself and calls .split on
  // what it is given — passing the DOM node threw "c.split is not a function"
  // and the card form never appeared. Stripe Elements takes a node; this does not.
  const mountId = useRef(`hyper-mount-${Math.random().toString(36).slice(2)}`).current;
  const ctx = useRef<{ hyper?: HyperInstance; widgets?: ReturnType<HyperInstance['widgets']> }>({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { clientSecret, publishableKey } = await hsCreateSetup(clientId);
        if (!publishableKey) throw new Error('Hyperswitch publishable key not configured');
        const Hyper = await loadHyper();
        if (cancelled || !mountRef.current) return;
        const hyper = Hyper(publishableKey);
        const widgets = hyper.widgets({ clientSecret });
        widgets.create('payment').mount(`#${mountId}`);
        ctx.current = { hyper, widgets };
        setReady(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start card setup');
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { hyper, widgets } = ctx.current;
    if (!hyper || !widgets) return;
    setBusy(true);
    setError(null);
    try {
      const res = await hyper.confirmPayment({ widgets, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
      if (res?.error) setError(res.error.message ?? 'Card could not be saved');
      else onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Card could not be saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PCard>
      <form onSubmit={submit}>
        <div ref={mountRef} id={mountId} />
        {!ready && !error && <Note>Preparing secure card form…</Note>}
        {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
        <button type="submit" className="btn btn--primary" disabled={!ready || busy} style={{ marginTop: 14 }}>
          {busy ? 'Saving…' : 'Save card'}
        </button>
      </form>
    </PCard>
  );
}
